import { useEffect, useState } from "react";
import { featureFlagApi } from "../../services/featureFlagApi";
import { Card } from "../../components/common/Card";
import { Toggle } from "../../components/common/Toggle";
import { Spinner } from "../../components/common/Spinner";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";

export function FeatureFlagsPage() {
  const [features, setFeatures] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [pendingRollout, setPendingRollout] = useState({});
  const [changelogs, setChangelogs] = useState({});
  const [comparisons, setComparisons] = useState({});

  useEffect(() => {
    featureFlagApi.listAll().then((r) => setFeatures(r.features)).catch(() => setFeatures([]));
  }, []);

  async function toggle(featureName, enabled) {
    setFeatures((prev) => prev.map((f) => (f.feature_name === featureName ? { ...f, default_enabled: enabled } : f)));
    await featureFlagApi.setEnabled(featureName, enabled);
  }

  function mergeFlag(featureName, patch) {
    setFeatures((prev) => prev.map((f) => (f.feature_name === featureName ? { ...f, ...patch } : f)));
  }

  async function applyRollout(featureName) {
    const pct = pendingRollout[featureName];
    const updated = await featureFlagApi.updateRollout(featureName, { rolloutPercentage: pct });
    mergeFlag(featureName, { rollout_percentage: updated.rollout_percentage, ab_test_active: updated.ab_test_active });
  }

  async function toggleAbTest(featureName, active) {
    mergeFlag(featureName, { ab_test_active: active });
    const updated = await featureFlagApi.updateRollout(featureName, { abTestActive: active });
    mergeFlag(featureName, { rollout_percentage: updated.rollout_percentage, ab_test_active: updated.ab_test_active });
  }

  async function toggleExpanded(featureName) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(featureName)) next.delete(featureName);
      else next.add(featureName);
      return next;
    });

    if (!changelogs[featureName]) {
      setChangelogs((prev) => ({ ...prev, [featureName]: "loading" }));
      featureFlagApi
        .changeLog(featureName)
        .then((r) => setChangelogs((prev) => ({ ...prev, [featureName]: r.changelog })))
        .catch(() => setChangelogs((prev) => ({ ...prev, [featureName]: [] })));
    }
    if (!comparisons[featureName]) {
      setComparisons((prev) => ({ ...prev, [featureName]: "loading" }));
      featureFlagApi
        .abComparison(featureName)
        .then((r) => setComparisons((prev) => ({ ...prev, [featureName]: r.comparison })))
        .catch(() => setComparisons((prev) => ({ ...prev, [featureName]: [] })));
    }
  }

  if (!features) return <Spinner />;

  const grouped = features.reduce((acc, f) => {
    (acc[f.category] ||= []).push(f);
    return acc;
  }, {});

  return (
    <div>
      <h1>Feature Flag Management</h1>
      <p style={{ color: "var(--color-text-muted)" }}>
        Toggling here changes app.FeatureFlags.default_enabled -- the default state new/Custom-theme users see, via
        the real POST /management/feature-flags endpoint. This does not retroactively change what any individual user
        already has toggled for themselves under the Custom theme.
      </p>

      {Object.entries(grouped).map(([category, items]) => (
        <Card key={category} title={category} className="section-spacing">
          {items.map((f) => {
            const isExpanded = expanded.has(f.feature_name);
            const rolloutValue = pendingRollout[f.feature_name] ?? f.rollout_percentage ?? 0;
            const changelog = changelogs[f.feature_name];
            const comparison = comparisons[f.feature_name];

            return (
              <div key={f.feature_name} style={{ borderBottom: "1px solid var(--color-border)", padding: "var(--space-2) 0" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Toggle
                    id={`flag-${f.feature_name}`}
                    label={f.feature_name}
                    checked={f.default_enabled}
                    onChange={(v) => toggle(f.feature_name, v)}
                  />
                  <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                    {f.ab_test_active && <Badge tone="honey">A/B Active</Badge>}
                    <Badge tone="info">{f.rollout_percentage ?? 0}% rollout</Badge>
                    <Button variant="ghost" size="sm" onClick={() => toggleExpanded(f.feature_name)}>
                      {isExpanded ? "Hide details" : "Rollout, A/B & Changelog"}
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: "var(--space-3)", display: "grid", gap: "var(--space-3)" }}>
                    <Card title="Gradual Rollout">
                      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={rolloutValue}
                          aria-label={`Rollout percentage for ${f.feature_name}`}
                          onChange={(e) =>
                            setPendingRollout((prev) => ({ ...prev, [f.feature_name]: Number(e.target.value) }))
                          }
                          style={{ flex: "1 1 200px" }}
                        />
                        <span style={{ minWidth: "3em" }}>{rolloutValue}%</span>
                        <Button size="sm" onClick={() => applyRollout(f.feature_name)}>
                          Apply Rollout %
                        </Button>
                      </div>
                      <div style={{ marginTop: "var(--space-3)" }}>
                        <Toggle
                          id={`ab-${f.feature_name}`}
                          label="A/B test active"
                          checked={!!f.ab_test_active}
                          onChange={(v) => toggleAbTest(f.feature_name, v)}
                        />
                      </div>
                    </Card>

                    <Card title="Usage Outcomes: Feature On vs. Off">
                      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                        Compares real app.FeatureUsageAnalytics completion/abandonment between users currently
                        bucketed on vs. off by the rollout percentage above. This is an observational usage-outcomes
                        comparison, not a randomized A/B test.
                      </p>
                      <DataTable
                        loading={comparison === "loading"}
                        rows={
                          Array.isArray(comparison)
                            ? comparison.map((c) => ({ ...c, _key: c.bucket_enabled ? "enabled" : "disabled" }))
                            : undefined
                        }
                        rowKey="_key"
                        emptyTitle="No usage data yet for either bucket"
                        columns={[
                          { key: "bucket", header: "Bucket", render: (r) => (r.bucket_enabled ? "Feature On" : "Feature Off") },
                          { key: "user_count", header: "Users" },
                          { key: "completed", header: "Completed" },
                          { key: "abandoned", header: "Abandoned" },
                        ]}
                      />
                    </Card>

                    <Card title="Change Log">
                      <DataTable
                        loading={changelog === "loading"}
                        rows={Array.isArray(changelog) ? changelog : undefined}
                        rowKey="change_id"
                        emptyTitle="No changes recorded for this flag yet"
                        columns={[
                          { key: "changed_date", header: "When", render: (r) => new Date(r.changed_date).toLocaleString() },
                          { key: "change_type", header: "Change" },
                          { key: "old_value", header: "Old Value" },
                          { key: "new_value", header: "New Value" },
                          { key: "changed_by_email", header: "Changed By" },
                        ]}
                      />
                    </Card>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      ))}
    </div>
  );
}
